import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

/**
 * Sidebar — left-side persistent navigation drawer (Standard variant).
 *
 * Derived from M3 Navigation Drawer (Standard persistent variant) per
 * Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/navigation-drawer/overview
 *   - Reference: github.com/material-components/material-web/tree/main/navigationdrawer
 *
 * Populus adaptations applied:
 *   - Dark theme surface (--color-sidebar #1E2A30) — matches Christophe's
 *     entity-detail screenshots. M3 default is light; Populus inverts for
 *     visual separation from the white content surface.
 *   - 240px fixed width on desktop; off-canvas slide on mobile is deferred
 *     to a follow-up sub-PR (when mobile nav is exercised).
 *   - Section labels in UPPERCASE Label Small (M3 Label Small spec).
 *   - Active item: 3px left-edge --brand-primary accent + subtle white
 *     overlay background (vs M3's filled pill — Populus prefers an edge
 *     accent that reads at a glance on dark surfaces).
 *   - AS logo + product label at top; user identity block at bottom
 *     (visible on every screen; matches Christophe's reference).
 *
 * Token discipline:
 *   - Surface → --color-sidebar (system; never per-org)
 *   - Active accent → --brand-primary (org-overridable per design-system.md
 *     § Color — System vs Brand tokens)
 *   - Text → hardcoded white + rgba(255,255,255,N) for hierarchy (sidebar
 *     dark theme is system-fixed; no semantic ink token applies on dark)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface SidebarUser {
  name: string;
  email: string;
  role: string;
}

export interface SidebarSection {
  label?: string;
  items: SidebarItem[];
}

export interface SidebarItem {
  to: string;
  label: string;
  badge?: ReactNode;
  end?: boolean;
}

export interface SidebarProps {
  sections: SidebarSection[];
  user?: SidebarUser;
  brandName?: string;
  brandTagline?: string;
}

export function Sidebar({
  sections,
  user,
  brandName = 'CoreOS',
  brandTagline = 'Platform',
}: SidebarProps) {
  return (
    <aside style={{
      gridArea: 'sidebar',
      background: 'var(--color-sidebar)',
      color: '#fff',
      width: 240,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'sticky',
      top: 0,
      alignSelf: 'start',
    }}>
      <header style={{
        padding: 'var(--sp-5) var(--sp-4) var(--sp-4)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
      }}>
        <div style={{
          width: 36, height: 36,
          borderRadius: 'var(--r-lg)',
          background: 'linear-gradient(160deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)',
          display: 'grid', placeItems: 'center',
          color: '#fff',
          fontWeight: 'var(--fw-bold)',
          fontSize: 'var(--fs-md)',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
        }}>CO</div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--fs-md)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            lineHeight: 'var(--lh-tight)',
          }}>{brandName}</div>
          <div style={{
            fontSize: 'var(--fs-xs)',
            color: 'rgba(255,255,255,0.5)',
            marginTop: 2,
          }}>{brandTagline}</div>
        </div>
      </header>

      <nav style={{
        flex: 1,
        overflowY: 'auto',
        padding: 'var(--sp-3) 0',
      }}>
        {sections.map((section, i) => (
          <div key={i} style={{ marginBottom: 'var(--sp-4)' }}>
            {section.label && (
              <div style={{
                padding: '0 var(--sp-4) var(--sp-2)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-semibold)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--ls-wider)',
                color: 'rgba(255,255,255,0.4)',
              }}>{section.label}</div>
            )}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {section.items.map(item => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    style={({ isActive }) => ({
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--sp-2)',
                      padding: 'var(--sp-2) var(--sp-4)',
                      paddingLeft: isActive ? `calc(var(--sp-4) - 3px)` : 'var(--sp-4)',
                      borderLeft: isActive ? '3px solid var(--brand-primary)' : '3px solid transparent',
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                      background: isActive ? 'rgba(255,255,255,0.04)' : 'transparent',
                      textDecoration: 'none',
                      fontSize: 'var(--fs-md)',
                      fontWeight: isActive ? 'var(--fw-medium)' : 'var(--fw-regular)',
                      transition: 'background-color 120ms ease, color 120ms ease',
                    })}
                  >
                    <span>{item.label}</span>
                    {item.badge !== undefined && (
                      <span style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'rgba(255,255,255,0.5)',
                      }}>{item.badge}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {user && (
        <footer style={{
          padding: 'var(--sp-3) var(--sp-4)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          }}>
            <div style={{
              width: 32, height: 32,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              display: 'grid', placeItems: 'center',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-semibold)',
            }}>{initials(user.name)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 'var(--fs-sm)',
                fontWeight: 'var(--fw-medium)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>{user.name}</div>
              <div style={{
                fontSize: 'var(--fs-xs)',
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--ls-wider)',
              }}>{user.role}</div>
            </div>
          </div>
        </footer>
      )}
    </aside>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
